import { useContext } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { apiPost, apiPatch, apiDelete } from 'api'
import { baseUrl } from 'api/helpers'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'
import axios from 'axios'

const acceptRule = async (token, municipalityId, policyDocumentId, data) => {
  const url = `municipalities/${municipalityId}/policy_documents/${policyDocumentId}/rules`
  const response = await apiPost({ url, token, objectName: 'policy_rule', params: data })
  return response
}

export const useAcceptPolicyRule = ({ policyDocumentId, options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  const { onSuccess: externalOnSuccess, ...remainingOptions } = options

  return useMutation(
    (data) => acceptRule(token, municipality_id, policyDocumentId, data),
    {
      onSuccess: (...args) => {
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
        queryClient.invalidateQueries(['policy-documents', municipality_id])
        externalOnSuccess?.(...args)
      },
      ...remainingOptions
    }
  )
}

const bulkAcceptRules = async (token, municipalityId, policyDocumentId, rules) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents/${policyDocumentId}/rules/bulk_create`
  const response = await axios.post(url, { rules }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useBulkAcceptPolicyRules = ({ policyDocumentId, options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (rules) => bulkAcceptRules(token, municipality_id, policyDocumentId, rules),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
        queryClient.invalidateQueries(['policy-documents', municipality_id])
      },
      ...options
    }
  )
}

const bulkAcceptPendingRules = async (token, municipalityId, policyDocumentId, ruleIds) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents/${policyDocumentId}/rules/bulk_accept`
  const response = await axios.post(url, { rule_ids: ruleIds }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useBulkAcceptPendingRules = ({ policyDocumentId, options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (ruleIds) => bulkAcceptPendingRules(token, municipality_id, policyDocumentId, ruleIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
        queryClient.invalidateQueries(['policy-documents', municipality_id])
      },
      ...options
    }
  )
}

const mergeRules = async (token, municipalityId, policyDocumentId, ruleIds, category) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents/${policyDocumentId}/rules/merge`
  const response = await axios.post(url, { rule_ids: ruleIds, category }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useMergePolicyRules = ({ policyDocumentId, options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    ({ ruleIds, category }) => mergeRules(token, municipality_id, policyDocumentId, ruleIds, category),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
        queryClient.invalidateQueries(['policy-documents', municipality_id])
      },
      ...options
    }
  )
}

const updateRule = async (token, municipalityId, policyDocumentId, ruleId, data) => {
  const url = `municipalities/${municipalityId}/policy_documents/${policyDocumentId}/rules/${ruleId}`
  const response = await apiPatch({ url, token, objectName: 'policy_rule', params: data })
  return response
}

export const useUpdatePolicyRule = ({ policyDocumentId, options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    ({ ruleId, data }) => updateRule(token, municipality_id, policyDocumentId, ruleId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
        queryClient.invalidateQueries(['policy-documents', municipality_id])
      },
      ...options
    }
  )
}

const deleteRule = async (token, municipalityId, policyDocumentId, ruleId) => {
  const url = `municipalities/${municipalityId}/policy_documents/${policyDocumentId}/rules/${ruleId}`
  const response = await apiDelete({ url, token })
  return response
}

export const useDeletePolicyRule = ({ policyDocumentId, options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (ruleId) => deleteRule(token, municipality_id, policyDocumentId, ruleId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
      },
      ...options
    }
  )
}

const linkAnalytic = async (token, analyticId, policyRuleId) => {
  const url = `${baseUrl}/analytics/${analyticId}/policy_rules`
  const response = await axios.post(url, { policy_rule_id: policyRuleId }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useLinkAnalytic = ({ policyDocumentId, options = {} } = {}) => {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    ({ analyticId, ruleId }) => linkAnalytic(token, analyticId, ruleId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
      },
      ...options
    }
  )
}

const matchRuleAnalytics = async (token, municipalityId, policyDocumentId, ruleId) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/policy_documents/${policyDocumentId}/rules/${ruleId}/match_analytics`
  const response = await axios.post(url, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

// Re-runs AI analytic linking for a single accepted rule (used to retry a rule
// that came back with no linked analytics).
export const useMatchRuleAnalytics = ({ policyDocumentId, options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    (ruleId) => matchRuleAnalytics(token, municipality_id, policyDocumentId, ruleId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
        queryClient.invalidateQueries(['policy-documents', municipality_id])
      },
      ...options
    }
  )
}

const unlinkAnalytic = async (token, linkId) => {
  const url = `policy_rules/${linkId}`
  const response = await apiDelete({ url, token })
  return response
}

export const useUnlinkAnalytic = ({ policyDocumentId, options = {} } = {}) => {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    ({ linkId }) => unlinkAnalytic(token, linkId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['policy-document', policyDocumentId])
      },
      ...options
    }
  )
}
